import React, { useMemo, useCallback, useState, useRef, useEffect } from "react";
import {
  Select,
  SelectProps,
  FormField,
  DatePicker,
  Icon,
} from "@cloudscape-design/components";
import type { TimeGranularity } from "./detailHelpers";
import { TIME_GRANULARITY_OPTIONS, MONTH_OPTIONS } from "../constants";

/**
 * Helper: Get ISO week number from a Date object.
 */
const getISOWeekNumber = (date: Date): number => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
};

/**
 * Helper: Get the Monday date of a given ISO week in a given year.
 */
const getDateOfISOWeek = (week: number, year: number): Date => {
  const jan4 = new Date(year, 0, 4);
  const dayOfWeek = jan4.getDay() || 7;
  const monday = new Date(jan4);
  monday.setDate(jan4.getDate() - dayOfWeek + 1 + (week - 1) * 7);
  return monday;
};

/**
 * Helper: Get the date range string for a week
 */
const getWeekDateRange = (week: number, year: number): string => {
  const monday = getDateOfISOWeek(week, year);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() - 1); // Sun (week start)
  const saturday = new Date(sunday);
  saturday.setDate(sunday.getDate() + 6); // Sat (week end)
  const fmt = (d: Date) => `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}`;
  return `${fmt(sunday)} - ${fmt(saturday)}`;
};

/**
 * Helper: Get max ISO weeks for a year
 */
const getMaxWeeksInYear = (year: number): number => {
  const dec28 = new Date(year, 11, 28);
  return getISOWeekNumber(dec28);
};

// ============================
// WeekPicker - DatePicker-like component for selecting Year-Week
// ============================
const WeekPicker: React.FC<{
  year: number;
  week: number | null;
  onChange: (y: number, w: number) => void;
}> = ({ year, week, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [displayYear, setDisplayYear] = useState(year);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setDisplayYear(year);
  }, [year]);

  const maxWeeks = useMemo(() => getMaxWeeksInYear(displayYear), [displayYear]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const displayValue = week ? `${year}-W${String(week).padStart(2, "0")}` : "";
  const weekDateRange = week ? getWeekDateRange(week, year) : "";

  const handleWeekSelect = (w: number) => {
    onChange(displayYear, w);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className="week-picker-container">
      {/* Input display - mimics DatePicker appearance */}
      <div
        className="week-picker-input"
        onClick={() => { if (!isOpen) setDisplayYear(year); setIsOpen(!isOpen); }}
      >
        <Icon name="calendar" variant="subtle" />
        <span className={`week-picker-value ${displayValue ? "week-picker-value-filled" : "week-picker-value-empty"}`}>
          {displayValue || "YYYY-Wxx"}
        </span>
        {weekDateRange && (
          <span className="week-picker-range">
            ({weekDateRange})
          </span>
        )}
      </div>

      {/* Dropdown panel */}
      {isOpen && (
        <div className="week-picker-dropdown">
          {/* Year navigation */}
          <div className="week-picker-year-nav">
            <span
              onClick={() => setDisplayYear(displayYear - 1)}
              className="week-picker-nav-btn"
            >
              ‹
            </span>
            <span className="week-picker-year-label">{displayYear}</span>
            <span
              onClick={() => setDisplayYear(displayYear + 1)}
              className="week-picker-nav-btn"
            >
              ›
            </span>
          </div>

          {/* Week grid */}
          <div className="week-picker-grid">
            {Array.from({ length: maxWeeks }, (_, i) => {
              const w = i + 1;
              const isSelected = w === week && displayYear === year;
              const isCurrent = w === getISOWeekNumber(new Date()) && displayYear === new Date().getFullYear();
              return (
                <div
                  key={w}
                  onClick={() => handleWeekSelect(w)}
                  className={`week-picker-cell ${isSelected ? "week-picker-cell-selected" : ""} ${isCurrent && !isSelected ? "week-picker-cell-current" : ""}`}
                  title={getWeekDateRange(w, displayYear)}
                >
                  W{String(w).padStart(2, "0")}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};


// ============================
// Exported Types
// ============================
export interface TimePickerValue {
  granularity: TimeGranularity;
  year: string;
  week: string | null;
  month: string | null;
  date: string | null;
  timeValue: string;
}

export interface TimePickerProps {
  granularity: TimeGranularity;
  onGranularityChange: (g: TimeGranularity) => void;
  year: SelectProps.Option | null;
  onYearChange: (y: SelectProps.Option) => void;
  week: SelectProps.Option | null;
  onWeekChange: (w: SelectProps.Option) => void;
  month: SelectProps.Option | null;
  onMonthChange: (m: SelectProps.Option) => void;
  date: string;
  onDateChange: (d: string) => void;
}

// ============================
// TimePicker Main Component
// ============================

/**
 * TimePicker component - encapsulates time granularity selection with:
 * - "day" mode: Cloudscape DatePicker (YYYY-MM-DD)
 * - "week" mode: Custom WeekPicker (YYYY-Wxx) with calendar-like dropdown
 * - "month" mode: Custom MonthPicker (YYYY-MM) with calendar-like dropdown
 *
 * All three modes have a similar DatePicker-like UX with an input field
 * showing the current value and a dropdown for selection.
 */
export const TimePicker: React.FC<TimePickerProps> = ({
  granularity,
  onGranularityChange,
  year,
  onYearChange,
  week,
  onWeekChange,
  month,
  onMonthChange,
  date,
  onDateChange,
}) => {
  const selectedYear = year?.value ? parseInt(year.value, 10) : new Date().getFullYear();
  const selectedWeekNum = week?.value ? parseInt(week.value.replace("W", ""), 10) : null;

  const handleGranularityChange = useCallback(
    (g: TimeGranularity) => {
      onGranularityChange(g);
      if (g === "week" && !week) {
        // Default to previous week only if no prior selection exists
        const now = new Date();
        const lastWeekDate = new Date(now);
        lastWeekDate.setDate(now.getDate() - 7);
        const prevWeekNum = getISOWeekNumber(lastWeekDate);
        const prevWeekYear = lastWeekDate.getFullYear();
        const weekValue = `W${String(prevWeekNum).padStart(2, "0")}`;
        onYearChange({ label: String(prevWeekYear), value: String(prevWeekYear) });
        onWeekChange({ label: weekValue, value: weekValue });
      }
      if (g === "day" && !date) {
        // Default to previous day only if no prior selection exists
        const now = new Date();
        const yesterday = new Date(now);
        yesterday.setDate(now.getDate() - 1);
        const yearStr = String(yesterday.getFullYear());
        const monthStr = String(yesterday.getMonth() + 1).padStart(2, "0");
        const dayStr = String(yesterday.getDate()).padStart(2, "0");
        onDateChange(`${yearStr}-${monthStr}-${dayStr}`);
      }
      if (g === "month" && !month) {
        const currentMonth = String(new Date().getMonth() + 1).padStart(2, "0");
        const opt = MONTH_OPTIONS.find((m) => (m as SelectProps.Option).value === currentMonth);
        if (opt) onMonthChange(opt as SelectProps.Option);
      }
    },
    [onGranularityChange, week, month, date, onWeekChange, onMonthChange, onYearChange, onDateChange]
  );

  const handleWeekChange = useCallback(
    (y: number, w: number) => {
      onYearChange({ label: String(y), value: String(y) });
      const weekValue = `W${String(w).padStart(2, "0")}`;
      onWeekChange({ label: weekValue, value: weekValue });
    },
    [onYearChange, onWeekChange]
  );

  // Local state to track the month DatePicker value for proper controlled behavior
  const computedMonthValue = useMemo(() => {
    if (month?.value && year?.value) {
      return `${year.value}-${month.value}`;
    }
    return "";
  }, [year, month]);

  const [monthInputValue, setMonthInputValue] = useState(computedMonthValue);

  // Sync local state when external state changes (e.g., granularity switch defaults)
  useEffect(() => {
    setMonthInputValue(computedMonthValue);
  }, [computedMonthValue]);

  // Handle DatePicker month change: parse YYYY-MM back to year + month options
  const handleMonthDatePickerChange = useCallback(
    (value: string) => {
      setMonthInputValue(value);
      if (!value) {
        onMonthChange(null as unknown as SelectProps.Option);
        return;
      }
      const parts = value.split("-");
      const yearStr = parts[0];
      const monthStr = parts[1];
      if (!yearStr || !monthStr || yearStr === "0000") {
        onMonthChange(null as unknown as SelectProps.Option);
        return;
      }
      onYearChange({ label: yearStr, value: yearStr });
      const monthOpt = MONTH_OPTIONS.find((o) => (o as SelectProps.Option).value === monthStr);
      if (monthOpt) onMonthChange(monthOpt as SelectProps.Option);
      else onMonthChange({ label: monthStr, value: monthStr } as SelectProps.Option);
    },
    [onYearChange, onMonthChange]
  );

  return (
    <FormField label="Time">
      <div className="time-picker-row">
        {/* Granularity Selector */}
        <div className="time-picker-granularity">
          <Select
            selectedOption={{ value: granularity, label: TIME_GRANULARITY_OPTIONS.find((o) => (o as SelectProps.Option).value === granularity)?.label }}
            onChange={({ detail }) => handleGranularityChange(detail.selectedOption.value as TimeGranularity)}
            options={TIME_GRANULARITY_OPTIONS}
          />
        </div>

        {/* Day mode: Cloudscape DatePicker */}
        {granularity === "day" && (
          <div className="time-picker-flex1" data-testid="program-filter-date">
            <DatePicker
              value={date}
              onChange={({ detail }) => onDateChange(detail.value)}
              placeholder="YYYY/MM/DD"
            />
          </div>
        )}

        {/* Week mode: Custom WeekPicker (DatePicker-like) */}
        {granularity === "week" && (
          <div data-testid="program-filter-week">
            <WeekPicker
              year={selectedYear}
              week={selectedWeekNum}
              onChange={handleWeekChange}
            />
          </div>
        )}

        {/* Month mode: Cloudscape DatePicker with granularity="month" */}
        {granularity === "month" && (
          <div className="time-picker-flex1" data-testid="program-filter-month">
            <DatePicker
              value={monthInputValue}
              onChange={({ detail }) => handleMonthDatePickerChange(detail.value)}
              granularity="month"
              placeholder="YYYY/MM"
            />
          </div>
        )}
      </div>
    </FormField>
  );
};

export default TimePicker;
